import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Empty,
    Input,
    Modal,
    Skeleton,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import { RollbackOutlined } from '@ant-design/icons';
import {
    aiAdminApi,
    SubsystemDiff,
    SubsystemId,
    SubsystemVersion,
} from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { DiffViewer } from '../shared/DiffViewer';

const { Text } = Typography;
const { TextArea } = Input;

interface Props {
    subsystem: SubsystemId;
    jwt: string;
}

const STATUS_COLOUR: Record<SubsystemVersion['status'], string> = {
    draft: 'orange',
    published: 'green',
    superseded: 'default',
    rolled_back: 'red',
    discarded: 'default',
};

function diffSummary(diff: SubsystemDiff | null): {
    headline: string;
    chips: Array<{ key: string; label: string; color: string }>;
} {
    if (!diff || !diff.hasChanges) {
        return { headline: 'No changes — rolling back would be a no-op.', chips: [] };
    }
    const chips: Array<{ key: string; label: string; color: string }> = [];
    if (diff.systemSettings.length > 0) {
        chips.push({
            key: 'settings',
            label: `${diff.systemSettings.length} system_settings changed`,
            color: 'blue',
        });
    }
    for (const [table, rows] of Object.entries(diff.configTableRows ?? {})) {
        if (!rows || rows.length === 0) continue;
        const added = rows.filter((r) => r.kind === 'added').length;
        const removed = rows.filter((r) => r.kind === 'removed').length;
        const changed = rows.filter((r) => r.kind === 'changed').length;
        const parts: string[] = [];
        if (added) parts.push(`${added} added`);
        if (changed) parts.push(`${changed} changed`);
        if (removed) parts.push(`${removed} removed`);
        chips.push({
            key: table,
            label: `${table}: ${parts.join(', ')}`,
            color: removed > 0 ? 'red' : added > 0 ? 'green' : 'orange',
        });
    }
    const total =
        diff.systemSettings.length +
        Object.values(diff.configTableRows ?? {}).reduce(
            (n, rows) => n + (rows?.length ?? 0),
            0,
        );
    return {
        headline: `${total} change${total === 1 ? '' : 's'} would be applied.`,
        chips,
    };
}

export const VersionsTab: React.FC<Props> = ({ subsystem, jwt }) => {
    const [rows, setRows] = useState<SubsystemVersion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [rollbackTarget, setRollbackTarget] = useState<SubsystemVersion | null>(null);
    const [rollbackNotes, setRollbackNotes] = useState('');
    const [isRollingBack, setIsRollingBack] = useState(false);
    const [diff, setDiff] = useState<SubsystemDiff | null>(null);
    const [isDiffLoading, setIsDiffLoading] = useState(false);
    const [diffError, setDiffError] = useState<string | null>(null);

    // Subscribe to the shared editor context so saving / publishing /
    // discarding a draft on the Configure tab refreshes this list — Antd
    // Tabs preserves mounted children when switching tabs, so without this
    // hook the table only re-fetches on full page reload.
    const editor = useSubsystemEditorContext();
    const draftVersionId = editor.detail?.draftVersionId ?? null;
    const publishedVersionId = editor.detail?.currentPublishedVersionId ?? null;
    const hasDraft = draftVersionId !== null;
    const summary = useMemo(() => diffSummary(diff), [diff]);

    const load = useCallback(async () => {
        if (!jwt) return;
        setIsLoading(true);
        try {
            setRows(await aiAdminApi.listVersions(jwt, subsystem, 100));
        } catch (err: any) {
            message.error(err.message || 'Failed to load versions');
        } finally {
            setIsLoading(false);
        }
    }, [jwt, subsystem]);

    useEffect(() => {
        load();
    }, [load, draftVersionId, publishedVersionId]);

    const openRollback = useCallback(
        async (row: SubsystemVersion) => {
            setRollbackTarget(row);
            setRollbackNotes('');
            setDiff(null);
            setDiffError(null);
            if (!publishedVersionId) {
                setDiffError('No currently-published version to compare against.');
                return;
            }
            setIsDiffLoading(true);
            try {
                // from = current published, to = rollback target
                // → diff reads "what would change if we apply the target".
                const result = await aiAdminApi.diffVersions(
                    jwt,
                    subsystem,
                    publishedVersionId,
                    row.id,
                );
                setDiff(result);
            } catch (err: any) {
                setDiffError(err.message || 'Failed to compute diff');
            } finally {
                setIsDiffLoading(false);
            }
        },
        [jwt, subsystem, publishedVersionId],
    );

    const closeRollback = () => {
        if (isRollingBack) return;
        setRollbackTarget(null);
        setRollbackNotes('');
        setDiff(null);
        setDiffError(null);
        setIsDiffLoading(false);
    };

    const confirmRollback = async () => {
        if (!rollbackTarget) return;
        setIsRollingBack(true);
        try {
            await aiAdminApi.rollback(
                jwt,
                subsystem,
                rollbackTarget.id,
                rollbackNotes.trim() || undefined,
            );
            message.success(`Rolled back to v${rollbackTarget.versionNumber}.`);
            setRollbackTarget(null);
            setRollbackNotes('');
            setDiff(null);
            await editor.reload();
            await load();
        } catch (err: any) {
            message.error(err.message || 'Rollback failed');
        } finally {
            setIsRollingBack(false);
        }
    };

    if (isLoading && rows.length === 0) return <Skeleton active />;
    if (!isLoading && rows.length === 0) {
        return <Empty description="No versions yet for this subsystem." />;
    }

    const currentPublishedNumber = rows.find((r) => r.id === publishedVersionId)?.versionNumber;

    return (
        <>
            <Table
                size="small"
                rowKey="id"
                pagination={{ pageSize: 25 }}
                dataSource={rows}
                columns={[
                    {
                        title: 'Version',
                        dataIndex: 'versionNumber',
                        key: 'versionNumber',
                        width: 90,
                        render: (v: number) => `v${v}`,
                    },
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        width: 130,
                        render: (s: SubsystemVersion['status']) => (
                            <Tag color={STATUS_COLOUR[s]}>{s}</Tag>
                        ),
                    },
                    {
                        title: 'Notes',
                        dataIndex: 'notes',
                        key: 'notes',
                        render: (n: string | null) => n ?? <Text type="secondary">—</Text>,
                    },
                    {
                        title: 'Created',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        width: 180,
                        render: (ts: string) => new Date(ts).toLocaleString(),
                    },
                    {
                        title: 'Published',
                        dataIndex: 'publishedAt',
                        key: 'publishedAt',
                        width: 180,
                        render: (ts: string | null) =>
                            ts ? new Date(ts).toLocaleString() : <Text type="secondary">—</Text>,
                    },
                    {
                        title: 'Actions',
                        key: 'actions',
                        width: 140,
                        render: (_: unknown, row: SubsystemVersion) => {
                            const isCurrentlyPublished = row.id === publishedVersionId;
                            const isDraft = row.status === 'draft';
                            const disabled = isCurrentlyPublished || isDraft || hasDraft;
                            const tooltip = isCurrentlyPublished
                                ? 'This is the current published version'
                                : isDraft
                                  ? 'Cannot roll back to a draft'
                                  : hasDraft
                                    ? 'Discard or publish the open draft first'
                                    : `Preview & roll back to v${row.versionNumber}`;
                            return (
                                <Tooltip title={tooltip}>
                                    <Button
                                        size="small"
                                        icon={<RollbackOutlined />}
                                        disabled={disabled}
                                        onClick={() => openRollback(row)}
                                    >
                                        Rollback
                                    </Button>
                                </Tooltip>
                            );
                        },
                    },
                ]}
            />
            <Modal
                open={rollbackTarget !== null}
                title={
                    rollbackTarget
                        ? `Roll back to v${rollbackTarget.versionNumber}` +
                          (currentPublishedNumber
                              ? ` (from current v${currentPublishedNumber})`
                              : '')
                        : 'Roll back'
                }
                okText="Roll back"
                okButtonProps={{
                    danger: true,
                    loading: isRollingBack,
                    disabled: isDiffLoading || diffError !== null,
                }}
                cancelButtonProps={{ disabled: isRollingBack }}
                onOk={confirmRollback}
                onCancel={closeRollback}
                maskClosable={!isRollingBack}
                width={900}
                destroyOnClose
            >
                <Text type="secondary">
                    Review the diff below before rolling back. A new published version will be
                    created from v{rollbackTarget?.versionNumber}'s payload and the current
                    published version will be marked superseded.
                </Text>

                {diffError && (
                    <Alert
                        type="error"
                        showIcon
                        message={diffError}
                        style={{ marginTop: 12 }}
                    />
                )}

                {!diffError && !isDiffLoading && diff && (
                    <Alert
                        type={diff.hasChanges ? 'info' : 'warning'}
                        showIcon
                        message={summary.headline}
                        description={
                            summary.chips.length > 0 ? (
                                <Space size="small" wrap>
                                    {summary.chips.map((chip) => (
                                        <Tag color={chip.color} key={chip.key}>
                                            {chip.label}
                                        </Tag>
                                    ))}
                                </Space>
                            ) : undefined
                        }
                        style={{ marginTop: 12 }}
                    />
                )}

                <div style={{ marginTop: 12 }}>
                    {isDiffLoading ? (
                        <Skeleton active />
                    ) : !diffError ? (
                        <DiffViewer diff={diff} />
                    ) : null}
                </div>

                <div style={{ marginTop: 16 }}>
                    <Text strong>Notes (optional)</Text>
                    <TextArea
                        rows={2}
                        value={rollbackNotes}
                        onChange={(e) => setRollbackNotes(e.target.value)}
                        placeholder="Why are you rolling back?"
                        disabled={isRollingBack}
                        style={{ marginTop: 4 }}
                    />
                </div>
            </Modal>
        </>
    );
};
